import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_URL = 'https://readdy.ai/api/form/d9v4sh963aqc1tdvapqg';

const PDF_ES = 'https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/7026c569-3383-49b3-89a1-0c1d42a11910_Regalo_DespertaresNocturnos_ES.pdf';
const PDF_EN = 'https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/21679848-5e48-45b0-8f85-de2e24999cf5_Regalo_DespertaresNocturnos_EN.pdf';

async function downloadGuide(lang: 'es' | 'en') {
  const url = lang === 'en' ? PDF_EN : PDF_ES;
  const filename = lang === 'en' ? 'Guia_Despertares_Nocturnos_EN.pdf' : 'Guia_Despertares_Nocturnos.pdf';
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank');
  }
}

export default function SleepSignalsGuideDownload() {
  const { t, i18n } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const lang: 'es' | 'en' = i18n.language === 'en' ? 'en' : 'es';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      downloadGuide(lang);
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'company_alt' && String(value).trim() === '') return;
      params.append(key, String(value));
    });

    setStatus('loading');
    setFormError('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        downloadGuide(lang);
      } else {
        setStatus('error');
        setFormError(serverMsg || t('senales.download.form.error'));
      }
    } catch {
      setStatus('error');
      setFormError(t('senales.download.form.error'));
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-terracotta/30 bg-[#FBF0DC] px-6 md:px-10 py-10 md:py-12">
      <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
        <div className="text-center lg:text-left max-w-2xl">
          <span className="inline-flex items-center gap-1.5 bg-terracotta/10 border border-terracotta/30 text-terracotta font-sans text-xs font-semibold px-3 py-1 rounded-full mb-4">
            <i className="ri-gift-line text-sm" />
            {t('senales.download.tag')}
          </span>
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#8B6F5C] mb-3 leading-tight">
            {t('senales.download.title')}
          </h3>
          <p className="font-sans text-sm md:text-base text-[#3D2B1F] leading-relaxed">
            {t('senales.download.text')}
          </p>
        </div>
        <div className="w-full lg:w-auto flex-shrink-0">
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="w-full lg:w-auto inline-flex items-center justify-center bg-terracotta text-white px-8 py-4 rounded-full font-sans text-sm font-semibold hover:bg-[#8B6F5C] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('senales.download.cta')}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="relative z-10 mt-8">
          {status === 'success' ? (
            <div className="bg-white rounded-xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 border border-terracotta/20">
              <p className="font-sans text-sm text-[#8B6F5C] font-semibold">
                {t('senales.download.form.success')}
              </p>
              <button
                onClick={() => downloadGuide(lang)}
                className="inline-flex items-center justify-center bg-terracotta text-white px-6 py-3 rounded-full font-sans text-sm font-semibold hover:bg-[#8B6F5C] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line mr-2" />
                {t('senales.download.form.downloadAgain')}
              </button>
            </div>
          ) : (
            <form
              id="guia-senales-sueno-form"
              onSubmit={handleSubmit}
              data-readdy-form="guia-senales-sueno"
              className="bg-white rounded-xl p-6 border border-terracotta/20"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t('senales.download.form.emailPlaceholder')}
                  className="flex-1 px-5 py-3.5 rounded-full border border-sand/60 bg-cream font-sans text-sm text-[#3D2B1F] outline-none focus:border-terracotta transition-colors duration-200"
                />
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-hp"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex items-center justify-center bg-terracotta text-white px-7 py-3.5 rounded-full font-sans text-sm font-semibold hover:bg-[#8B6F5C] transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2" />
                      {t('senales.download.form.loading')}
                    </>
                  ) : (
                    t('senales.download.form.submit')
                  )}
                </button>
              </div>
              {status === 'error' && formError && (
                <p className="mt-3 font-sans text-xs text-terracotta">
                  {formError}
                </p>
              )}
              <p className="mt-3 font-sans text-xs text-[#8B6F5C]">
                {t('senales.download.form.privacy')}
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}